import React, {ReactNode} from "react";

// global style, it's very IMPORTANT that this should load first
// before loading any other component
import "./src/styles/common.scss";

import type {GatsbyBrowser} from "gatsby";
import {initSentry, logErrorToSentry} from "./src/utils/sentry";
import {updateUtmCookies} from "./src/utils/UTM-tracking";
import {logEvent} from "./src/utils/amplitude";
import ErrorBoundary from "./src/components/error-boundary/ErrorBoundary";
import GDPRWrapper from "./src/components/gdpr/wrapper/GDPRWrapper";
import FlexportAsciiShip from "./src/constants/flexport-ascii-ship";
import {logPath} from "./src/utils/path-logging";
import WindowSizeProvider from "./src/contexts/window-context";
import {AlgoliaContext} from "./src/contexts/context";
import {logDataLayerEvent} from "./src/utils/gtm-data-layer";
import {isBot} from "./src/utils/utils";

import {
  GATSBY_DATADOG_CLIENT_TOKEN,
  GATSBY_DATADOG_APPLICATION_ID,
  NODE_ENV,
} from "./src/config";

export const wrapRootElement: GatsbyBrowser["wrapRootElement"] = ({
  element,
}: {
  element: ReactNode;
}) => {
  return (
    <ErrorBoundary>
      <GDPRWrapper>
        <WindowSizeProvider>{element}</WindowSizeProvider>
      </GDPRWrapper>
    </ErrorBoundary>
  );
};

// Import the shared wrapPageElement from gatsby-shared
import {wrapPageElement as sharedWrapPageElement} from "./gatsby-shared";

export const wrapPageElement: GatsbyBrowser["wrapPageElement"] = ({
  element,
  props,
}: {
  element: ReactNode;
  props: any;
}) => {
  // First wrap with shared scripts, then add browser-specific wrapping
  const wrappedElement = (
    <>
      {/*
        passing along algolia context here
        grab it from the page context
        this way, we don't need to create new env prefixed with GATSBY_
      */}
      <AlgoliaContext.Provider value={props.pageContext.algoliaIndex!}>
        {element}
      </AlgoliaContext.Provider>
    </>
  );

  // Apply shared wrapping with scripts
  return sharedWrapPageElement({element: wrappedElement, props});
};

let gtmEventTimeoutId: ReturnType<typeof setTimeout> | null = null;

const runWhenIdle = (fn: () => void) => {
  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(fn, {timeout: 3000});
  } else {
    requestAnimationFrame(() => setTimeout(fn, 0));
  }
};

// called when gatsby client runtime first starts https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/#onClientEntry
export const onClientEntry: GatsbyBrowser["onClientEntry"] = () => {
  initSentry();
};

export const onInitialClientRender: GatsbyBrowser["onInitialClientRender"] =
  () => {
    try {
      updateUtmCookies();
    } catch (error) {
      logErrorToSentry(
        error instanceof Error ? error : new Error(String(error))
      );
    }

    if (NODE_ENV !== "production") return;

    const isRobot = isBot(navigator.userAgent);

    runWhenIdle(async () => {
      try {
        const {datadogRum} = await import("@datadog/browser-rum-slim");
        datadogRum.init({
          applicationId: GATSBY_DATADOG_APPLICATION_ID!,
          clientToken: GATSBY_DATADOG_CLIENT_TOKEN!,
          site: "datadoghq.com",
          service: "flexport-static-site",
          env: "production",
          defaultPrivacyLevel: "mask-user-input",
          sessionSampleRate: isRobot ? 0 : 100,
        });
        // Removed `startSessionReplayRecording()`, not included in slim version
        const {setRum} = await import("./src/utils/datadog-rum");
        setRum(datadogRum);
        // Move to idle to avoid triggering Sentry
        if (process.env.NODE_ENV === "production") {
          /* eslint-disable-next-line no-console */
          console.log(FlexportAsciiShip);
        }
      } catch {}
    });
  };

export const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = ({
  location,
}: {
  location: Location;
}) => {
  logEvent("gatsbyRouteUpdate", {path: location.pathname});
  logPath(location.pathname);

  if (gtmEventTimeoutId) {
    clearTimeout(gtmEventTimeoutId);
  }

  // schedule heavier analytics on idle
  const fireAnalyticEvents = () => {
    try {
      logDataLayerEvent("gatsbyRouteLoaded", {}, NODE_ENV != "production");
    } catch {}
    gtmEventTimeoutId = null;
  };

  // Use idle instead to improve INP
  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(fireAnalyticEvents, {timeout: 800});
  } else {
    gtmEventTimeoutId = setTimeout(fireAnalyticEvents, 300);
  }
};

export const shouldUpdateScroll: GatsbyBrowser["shouldUpdateScroll"] = ({
  routerProps: {location},
  getSavedScrollPosition,
}) => {
  const state = (location.state ?? {}) as {preventScrollReset?: boolean};

  if (state.preventScrollReset) {
    // tell Gatsby NOT to manage scrolling for this navigation
    return false;
  }

  // For all other navigation events, use Gatsby's default behavior
  const savedPosition = getSavedScrollPosition(location);
  return savedPosition ?? [0, 0];
};
